import type { Team } from 'api-client';

import {
  clearAllPersistedCacheByPrefix,
  getPersistedCache,
  setPersistedCache,
} from './persistedCache';

const CACHE_VERSION = 1;
const STORAGE_KEY_PREFIX = 'ahoy-cache-available-orgs-';
const COMPONENT = 'persisted-available-orgs';

// Available-orgs membership changes rarely but can become stale (user added
// to a new org, removed from an org, role changes). 7 days bounds how stale
// the seed data can be on first paint; OrgContext.loadTeams overwrites with
// fresh data on every mount, so this only matters for users who haven't
// opened the app in over a week.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function isAvailableOrgsShape(raw: unknown): raw is Team[] {
  if (!Array.isArray(raw)) {
    return false;
  }
  return raw.every(
    item =>
      typeof item === 'object' &&
      item !== null &&
      typeof (item as Record<string, unknown>).id === 'number' &&
      typeof (item as Record<string, unknown>).name === 'string'
  );
}

export function getPersistedAvailableOrgs(
  userId: number | undefined
): Team[] | undefined {
  if (userId === undefined) {
    return undefined;
  }
  const key = `${STORAGE_KEY_PREFIX}${userId}`;
  return getPersistedCache<Team[]>(
    key,
    CACHE_VERSION,
    isAvailableOrgsShape,
    COMPONENT,
    MAX_AGE_MS
  );
}

export function setPersistedAvailableOrgs(userId: number, orgs: Team[]): void {
  const key = `${STORAGE_KEY_PREFIX}${userId}`;
  setPersistedCache<Team[]>(key, orgs, CACHE_VERSION, COMPONENT);
}

export function clearAllPersistedAvailableOrgs(): void {
  clearAllPersistedCacheByPrefix(STORAGE_KEY_PREFIX, COMPONENT);
}
